<template>
  <div :class="containerClass">

    <h2>Add an Admissions Assessment</h2>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Assessment Type*" label-for="type" label-class="form-label">
        <v-select
          id="type"
          class="form-control v-select-custom"
          :class="!$v.type.required && $v.type.$dirty ? 'form-control is-invalid' : ''"
          v-model="type"
          :options="assessmentTypes"
          placeholder="Select an assessment type"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!type" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.type.required && $v.type.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group v-if="type === 'Other'" label="Other" label-for="typeOther" label-class="form-label">
        <b-form-input
          id="typeOther"
          placeholder="Enter assessment name"
          v-model="typeOther"
          :state="!$v.typeOther.required && $v.typeOther.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Score" label-for="score" label-class="form-label">
        <b-form-input id="score" type="number" step="0.01" v-model.number="score" placeholder="Enter score" />
      </b-form-group>

      <b-form-group label="Expiration Date" label-for="expirationDate" class="form-label">
        <date-picker
          id="expirationDate"
          v-model="expirationDate"
          type="date"
          format="MM/DD/YYYY"
          value-type="YYYY-MM-DD"
          style="width: 100%"
          lang="en"
          placeholder="MM/DD/YYYY"
        />
      </b-form-group>

      <b-form-group
        label="Upload Score Report"
        label-for="document"
        label-class="form-label"
        class="row-align-items-center"
      >
        <b-progress :max="100" animated v-if="isUploading">
          <b-progress-bar :value="uploadPercent">
            <span>Uploading (<strong>{{ uploadPercent }} %</strong>)</span>
          </b-progress-bar>
        </b-progress>
        <span v-else-if="docUrl">
          <div class="row">
            <div class="col-md-10">
              <a
                href="#"
                @click.prevent="downloadFile({ fileUrl: docUrl, removeTimestamp: true })"
                v-b-popover.hover.top="'Download'"
                ><i class="fas fa-file text-primary"></i> {{ parseFileNameFromUrl(docUrl, true) }}
              </a>
            </div>
            <div class="text-right col-md-2">
              <a href="#" class="text-danger" @click.prevent="removeFile()"
                ><i class="fas fa-times-circle"></i> Remove
              </a>
            </div>
          </div>
        </span>
        <b-media class="align-items-center" vertical-align="center" v-else>
          <b-form-file
            placeholder="Select Document (max size: 10MB, only .pdf and image files allowed)"
            :browse-text="$t('generalMsgs.browse')"
            v-model="selectedUploadFile"
            @input="uploadFile()"
            :disabled="isUploading"
            accept=".pdf, image/*"
          />
        </b-media>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : 'Add' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import axios from 'axios';
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import { parseFileNameFromUrl } from '../../../common/utils';
import { required, requiredIf } from 'vuelidate/lib/validators';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/en';

export default {
  extends: Page,
  components: { DatePicker },

  data() {
    return {
      title: 'Add Admissions Assessment',
      isLoading: false,
      type: null,
      typeOther: '',
      score: null,
      expirationDate: null,
      docUrl: '',
      isUploading: false,
      selectedUploadFile: null,
      uploadPercent: 0,
      baseAssessmentTypes: [
        'TEAS',
        'HESI A2',
        'Kaplan Nursing Entrance Exam',
        'SAT',
        'ACT',
        'GRE',
        'GMAT',
        'MCAT',
        'LSAT',
        'DAT',
        'OAT',
        'CASPer',
        'TOEFL',
        'IELTS',
        'Other',
      ],
      allowedAssessmentValues: [],
    };
  },

  validations() {
    return {
      type: { required },
      typeOther: { required: requiredIf(() => this.type === 'Other') },
    };
  },

  computed: {
    assessmentTypes() {
      // When the checklist config restricts allowed assessments, only those
      // are selectable (matched by name; "Other" excluded while restricted).
      if (this.allowedAssessmentValues.length) {
        const allowed = new Set(this.allowedAssessmentValues.map(v => String(v).trim().toLowerCase()));
        return this.baseAssessmentTypes.filter(t => allowed.has(String(t).trim().toLowerCase()));
      }
      return this.baseAssessmentTypes;
    },
    checklistContext() {
      const uniId = Number(this.$route.query.uniId);
      const programId = Number(this.$route.query.program_id);
      const checklistConfigId = Number(this.$route.query.checklist_config_id);
      return {
        uniId: Number.isFinite(uniId) && uniId > 0 ? uniId : null,
        programId: Number.isFinite(programId) && programId > 0 ? programId : null,
        checklistConfigId:
          Number.isFinite(checklistConfigId) && checklistConfigId > 0 ? checklistConfigId : null,
      };
    },
  },

  watch: {
    '$route.query.checklist_config_id'() {
      this.fetchChecklistConfigExtra();
    },
  },

  methods: {
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('student', [
      'createAdmissionsAssessment',
      'getAdmissionsAssessment',
      'updateAdmissionsAssessment',
      'getStudentUploadPresignedUrl',
      'updateShowEarnedPointToast',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('uni', ['getUniversityChecklistConfigExtra']),
    parseFileNameFromUrl,
    get,
    getChecklistAllowedValues(res) {
      const candidates = [
        get(res, 'extra.allowed_values', null),
        get(res, 'data.extra.allowed_values', null),
        get(res, 'data.allowed_values', null),
        get(res, 'allowed_values', null),
      ];
      const found = candidates.find(Array.isArray);
      return found || [];
    },
    async fetchChecklistConfigExtra() {
      const { checklistConfigId } = this.checklistContext;
      if (!checklistConfigId) return;
      try {
        const res = await this.getUniversityChecklistConfigExtra({ checklist_config_id: checklistConfigId });
        this.allowedAssessmentValues = this.getChecklistAllowedValues(res);
      } catch (_err) {
        this.allowedAssessmentValues = [];
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    async uploadFile(uploadType = 'documents') {
      this.uploadCancelTokenSource = axios.CancelToken.source();
      this.isUploading = true;

      try {
        const urlResp = await this.getStudentUploadPresignedUrl({
          file_name: this.selectedUploadFile.name,
          content_type: this.selectedUploadFile.type,
          upload_type: uploadType,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.selectedUploadFile,
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource.token,
          },
        });
        this.docUrl = urlResp.upload_url.split('?')[0];
      } catch (error) {
        this.selectedUploadFile = null;
      }

      this.uploadCancelTokenSource = null;
      this.uploadPercent = 0;
      this.isUploading = false;
    },
    removeFile() {
      this.selectedUploadFile = null;
      this.docUrl = null;
    },
    async fetchExisting() {
      try {
        const res = await this.getAdmissionsAssessment(this.$route.params.id);
        this.type = res.data.type;
        this.typeOther = res.data.type_other;
        this.score = res.data.score;
        this.expirationDate = res.data.expiration_date;
        this.docUrl = res.data.score_report_url;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            type: this.type,
            type_other: this.typeOther,
            score: this.score,
            expiration_date: this.expirationDate,
            score_report_url: this.docUrl,
          };

          if (this.$route.params.id) {
            await this.updateAdmissionsAssessment({ id: this.$route.params.id, data });
            this.makeToast({ variant: 'success', msg: 'Assessment updated!' });
          } else {
            const createPayload = {
              ...data,
              ...(this.checklistContext.uniId && {
                entity_type: 'university',
                entity_id: this.checklistContext.uniId,
              }),
              ...(this.checklistContext.uniId && this.checklistContext.programId && {
                program_id: this.checklistContext.programId,
              }),
            };
            const resp = await this.createAdmissionsAssessment(createPayload);
            this.makeToast({ variant: 'success', msg: 'Assessment added!' });
            if (resp.data.data.points_earned > 0) {
              this.updateShowEarnedPointToast(resp.data.data.points_earned);
            } else {
              this.makeToast({
                variant: 'info',
                msg: 'Saved successfully. No checklist points awarded for this program requirement.',
              });
            }
          }
          setTimeout(() => {
            this.$router.push({ name: 'my-admissions-assessments' });
          }, 1000);
        } catch (err) {
          this.makeToast({
            variant: 'danger',
            msg: get(err, 'response.data.message') || get(err, 'response.data.error') || this.$t('generalMsgs.genErrorMsg'),
          });
        }
        this.isLoading = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields.' });
      }
    },
  },

  async mounted() {
    if (this.$route.params.id) {
      this.fetchExisting();
    }
    this.fetchChecklistConfigExtra();
  },
};
</script>
